import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { CRMLayout } from "@/components/CRMLayout";
import BoutonArreter from "@/components/ArretAgent";
import { toast } from "sonner";
import {
  Bot, Brain, FileText, Loader2, MessageSquare, Pencil, Plus, Send,
  Settings, ShieldCheck, Trash2, Upload, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import AgentCreationWizard from "@/components/AgentCreationWizard";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const COULEURS = {
  indigo: "from-indigo-500 to-indigo-700",
  violet: "from-violet-500 to-purple-700",
  emerald: "from-emerald-500 to-teal-700",
  sky: "from-sky-500 to-blue-700",
  amber: "from-amber-500 to-orange-700",
  rose: "from-rose-500 to-pink-700",
};
const FORM_VIDE = { name: "", icon: "🤖", color: "indigo", mission: "", profile: "", memory: "" };

function AgentForm({ agent, onClose, onSave }) {
  const [form, setForm] = useState(agent ? {
    name: agent.name || "", icon: agent.icon || "🤖", color: agent.color || "indigo",
    mission: agent.mission || "", profile: agent.profile || "", memory: agent.memory || "",
    company_project: agent.company_project || "", engine: agent.engine || "claude",
    acces_odoo: agent.acces_odoo || "aucun", acces_api: agent.acces_api || {}, allowed_paths: agent.allowed_paths || [],
  } : { ...FORM_VIDE, company_project: "", engine: "claude", acces_odoo: "aucun", acces_api: {}, allowed_paths: [] });
  const [saving, setSaving] = useState(false);
  const change = (key) => (event) => setForm((old) => ({ ...old, [key]: event.target.value }));
  const submit = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.mission.trim()) {
      toast.error("Le nom et la mission sont obligatoires");
      return;
    }
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-950/55 p-4 flex items-center justify-center">
      <form onSubmit={submit} className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900">{agent ? "Configurer l’agent" : "Créer un agent"}</h2>
            <p className="text-xs text-slate-500 mt-0.5">Définissez précisément son rôle et ce qu’il doit retenir.</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-[90px_1fr] gap-4">
            <label className="text-sm font-medium text-slate-700">Icône
              <input value={form.icon} onChange={change("icon")} maxLength={8}
                     className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-2xl text-center" />
            </label>
            <label className="text-sm font-medium text-slate-700">Nom de l’agent
              <input value={form.name} onChange={change("name")} placeholder="Ex. Contrôleur de trésorerie"
                     className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm" />
            </label>
          </div>
          <div>
            <div className="text-sm font-medium text-slate-700 mb-2">Couleur</div>
            <div className="flex gap-2 flex-wrap">
              {Object.keys(COULEURS).map((color) => (
                <button type="button" key={color} onClick={() => setForm((old) => ({ ...old, color }))}
                        className={`w-8 h-8 rounded-full bg-gradient-to-br ${COULEURS[color]} ${form.color === color ? "ring-2 ring-offset-2 ring-slate-800" : ""}`}
                        aria-label={color} />
              ))}
            </div>
          </div>
          <label className="block text-sm font-medium text-slate-700">Mission permanente
            <textarea value={form.mission} onChange={change("mission")} rows={3}
                      placeholder="Ce que cet agent doit surveiller, analyser et produire…"
                      className="mt-1 w-full resize-y rounded-lg border border-slate-200 px-3 py-2.5 text-sm" />
          </label>
          <label className="block text-sm font-medium text-slate-700">Profil et façon de travailler
            <textarea value={form.profile} onChange={change("profile")} rows={4}
                      placeholder="Son expertise, son ton, ses règles et ses limites…"
                      className="mt-1 w-full resize-y rounded-lg border border-slate-200 px-3 py-2.5 text-sm" />
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm font-medium text-slate-700">Périmètre (sociétés, clients, projets)
              <input value={form.company_project} onChange={change("company_project")} placeholder="Ex. RL Construction, RM Location"
                     className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm" />
            </label>
            <label className="block text-sm font-medium text-slate-700">Moteur
              <select value={form.engine} onChange={change("engine")} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm">
                <option value="claude">Claude</option>
                <option value="codex">Codex (compte ChatGPT)</option>
              </select>
            </label>
          </div>
          <div>
            <div className="text-sm font-medium text-slate-700">Connexions : ce que l'agent peut lire ou faire lui-même</div>
            <div className="mt-2 divide-y divide-slate-100 rounded-lg border border-slate-200">
              {[
                ["odoo", "Odoo", "Comptabilité, ventes, stock… Action = OD en brouillon, tâches pour Élodie", ["aucun", "lecture", "brouillon"]],
                ["meta", "Meta Ads", "Campagnes des 3 comptes. Action = pause, réactivation, budget (×2 et 150 €/j max)", ["aucun", "lecture", "action"]],
                ["gmail", "Gmail", "contact.ccrm1234. Action = envoyer un email que tu lui as demandé", ["aucun", "action"]],
                ["github", "GitHub", "Dépôts RunOdoo. Action = créer un ticket (issue)", ["aucun", "lecture", "action"]],
                ["stripe", "Stripe", "Compte RM Location. Action = créer un lien de paiement", ["aucun", "lecture", "action"]],
              ].map(([cle, nom, aide, niveaux]) => {
                const valeur = cle === "odoo" ? form.acces_odoo : (form.acces_api?.[cle] || "aucun");
                const changer = (v) => setForm((old) => cle === "odoo" ? { ...old, acces_odoo: v }
                  : { ...old, acces_api: { ...(old.acces_api || {}), [cle]: v } });
                return (
                  <div key={cle} className="flex flex-wrap items-center gap-2 px-3 py-2">
                    <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-slate-800">{nom}</div><div className="text-[11px] text-slate-500">{aide}</div></div>
                    <div className="inline-flex rounded-lg border border-slate-200 bg-slate-100 p-0.5">
                      {niveaux.map((n) => (
                        <button type="button" key={n} onClick={() => changer(n)}
                          className={`rounded-md px-2.5 py-1 text-xs ${valeur === n ? "bg-white font-semibold text-indigo-700 shadow-sm" : "text-slate-500"}`}>
                          {{ aucun: "Aucun", lecture: "Lecture", brouillon: "Action", action: "Action" }[n]}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <span className="block text-xs text-slate-400 mt-1">Pendant une conversation, l'agent agit quand tu le lui demandes et te rend compte. Lors des points automatiques, il propose seulement. Fonctionne avec Claude et Codex ; tout est tracé dans « À valider ».</span>
          </div>
          <label className="block text-sm font-medium text-slate-700">Dossiers de travail sur le serveur (un par ligne)
            <textarea value={(form.allowed_paths || []).join("\n")} rows={3}
                      onChange={(e) => setForm((old) => ({ ...old, allowed_paths: e.target.value.split("\n") }))}
                      placeholder="/opt/odoo19/addons"
                      className="mt-1 w-full resize-y rounded-lg border border-slate-200 px-3 py-2.5 font-mono text-xs" />
          </label>
          <label className="block text-sm font-medium text-slate-700">Mémoire permanente
            <textarea value={form.memory} onChange={change("memory")} rows={5}
                      placeholder="Les informations qui doivent rester vraies dans toutes ses nouvelles discussions…"
                      className="mt-1 w-full resize-y rounded-lg border border-slate-200 px-3 py-2.5 text-sm" />
            <span className="block text-xs text-slate-400 mt-1">Vous pouvez la compléter à tout moment. L’agent conserve aussi des repères automatiques de ses échanges.</span>
          </label>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
          <Button type="submit" disabled={saving}>{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{agent ? "Enregistrer" : "Créer l’agent"}</Button>
        </div>
      </form>
    </div>
  );
}

export default function AtelierPage() {
  const { token } = useAuth();
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [agents, setAgents] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [events, setEvents] = useState([]);
  const [input, setInput] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [running, setRunning] = useState(false);
  const controleurRef = useRef(null);
  const executionRef = useRef(null);
  const arretManuelRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [formAgent, setFormAgent] = useState(undefined);
  const [formOpen, setFormOpen] = useState(false);
  const [assistantCreationOuvert, setAssistantCreationOuvert] = useState(false);
  const [missions, setMissions] = useState([]);
  const [sessionSetupOpen, setSessionSetupOpen] = useState(false);
  const [sessionMissionId, setSessionMissionId] = useState("");
  const [sessionPermanent, setSessionPermanent] = useState(false);
  const fileRef = useRef(null);
  const chatRef = useRef(null);

  const selected = agents.find((agent) => agent.id === selectedId) || null;

  const loadAgents = useCallback(async (preferId) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/atelier/agents`, auth);
      setAgents(data);
      setSelectedId((old) => preferId || (data.some((a) => a.id === old) ? old : data[0]?.id || null));
    } catch { toast.error("Impossible de charger l’atelier"); }
    finally { setLoading(false); }
  }, [auth]);

  const loadConversations = useCallback(async (agentId) => {
    if (!agentId) { setConversations([]); return; }
    try {
      const { data } = await axios.get(`${API_URL}/api/atelier/agents/${agentId}/conversations`, auth);
      setConversations(data);
    } catch { setConversations([]); }
  }, [auth]);

  const loadMissions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/team/missions`, auth);
      setMissions((data.missions || []).filter((mission) => mission.status === "active"));
    } catch { setMissions([]); }
  }, [auth]);

  useEffect(() => { loadAgents(); loadMissions(); }, [loadAgents, loadMissions]);
  useEffect(() => {
    setSessionId(null); setEvents([]); setInput(""); setPendingFiles([]);
    setSessionMissionId(""); setSessionPermanent(false);
    loadConversations(selectedId);
  }, [selectedId, loadConversations]);
  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [events, running]);

  const saveAgent = async (form) => {
    try {
      const { data } = formAgent
        ? await axios.put(`${API_URL}/api/atelier/agents/${formAgent.id}`, form, auth)
        : await axios.post(`${API_URL}/api/atelier/agents`, form, auth);
      toast.success(formAgent ? "Agent mis à jour" : "Agent créé");
      setFormOpen(false); setFormAgent(undefined);
      await loadAgents(data.id);
    } catch (error) { toast.error(error.response?.data?.detail || "Enregistrement impossible"); }
  };

  const createGuidedAgent = async (configuration) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/atelier/agents`, configuration, auth);
      toast.success(`${data.name} est prêt`);
      await loadAgents(data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Création impossible");
      throw error;
    }
  };

  const deleteAgent = async (agent) => {
    if (!window.confirm(`Supprimer « ${agent.name} » de l’atelier ? Son historique restera récupérable côté serveur.`)) return;
    try {
      await axios.delete(`${API_URL}/api/atelier/agents/${agent.id}`, auth);
      toast.success("Agent retiré de l’atelier");
      await loadAgents();
    } catch { toast.error("Suppression impossible"); }
  };

  const openConversation = async (sid) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/atelier/agents/${selectedId}/conversations/${sid}`, auth);
      setEvents((data.messages || []).map((message) => ({
        type: message.role === "user" ? "user" : "assistant", text: message.text,
      })));
      setSessionId(sid);
      setSessionMissionId(data.mission_id || "");
      setSessionPermanent(Boolean(data.permanent));
    } catch { toast.error("Conversation introuvable"); }
  };

  const newConversation = () => {
    setSessionMissionId("");
    setSessionPermanent(false);
    setSessionSetupOpen(true);
  };
  const startConversation = () => {
    setSessionId(null); setEvents([]); setInput(""); setPendingFiles([]);
    setSessionSetupOpen(false);
  };
  const push = (event) => setEvents((old) => [...old, event]);
  const handleStreamEvent = (evt) => {
    if (evt.type === "done") { if (evt.session_id) setSessionId(evt.session_id); loadConversations(selectedId); loadAgents(selectedId); setRunning(false); return true; }
    if (evt.type === "error") { push({ type: "error", text: evt.message }); setRunning(false); return false; }
    if (evt.type === "assistant" && evt.message?.content) {
      evt.message.content.forEach((content) => {
        if (content.type === "text" && content.text) push({ type: "assistant", text: content.text });
        if (content.type === "tool_use") push({ type: "tool", text: `${content.name} ${content.input?.file_path || content.input?.command || ""}` });
      });
    }
    return false;
  };

  const send = async (message) => {
    const prompt = message.trim() || (pendingFiles.length ? "Analyse les pièces jointes et indique-moi les actions à retenir." : "");
    if (!prompt || !selected || running) return;
    const filesForMessage = pendingFiles.slice();
    const visiblePrompt = filesForMessage.length
      ? `${prompt}\n\n📎 ${filesForMessage.map((file) => file.name).join(", ")}` : prompt;
    setRunning(true); push({ type: "user", text: visiblePrompt }); setInput("");
    const controller = new AbortController();
    controleurRef.current = controller; arretManuelRef.current = false;
    executionRef.current = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
    const timeout = window.setTimeout(() => controller.abort(), 5 * 60 * 1000);
    let completed = false;
    try {
      const response = await fetch(`${API_URL}/api/atelier/agents/${selected.id}/run`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          prompt, resume: sessionId, file_ids: filesForMessage.map((file) => file.id),
          mission_id: sessionMissionId || null, session_permanent: sessionPermanent,
          run_id: executionRef.current,
        }),
        signal: controller.signal,
      });
      if (!response.ok) {
        let detail = "L’agent n’a pas pu démarrer";
        try { detail = (await response.json()).detail || detail; } catch { /* réponse non JSON */ }
        throw new Error(detail);
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split("\n\n"); buffer = blocks.pop();
        blocks.forEach((block) => {
          if (!block.startsWith("data: ")) return;
          try { if (handleStreamEvent(JSON.parse(block.slice(6)))) completed = true; } catch { /* fragment incomplet */ }
        });
      }
      if (buffer.trim().startsWith("data: ")) {
        try { if (handleStreamEvent(JSON.parse(buffer.trim().slice(6)))) completed = true; } catch { /* dernier fragment incomplet */ }
      }
      if (!completed) throw new Error("La réponse s’est interrompue avant la fin");
      setPendingFiles([]);
    } catch (error) {
      if (error.name === "AbortError" && arretManuelRef.current) {
        push({ type: "error", text: "⏹ Arrêté à ta demande" });
        return;
      }
      const messageErreur = error.name === "AbortError" ? "L’analyse a dépassé 5 minutes et a été arrêtée" : error.message || "Connexion avec l’agent interrompue";
      push({ type: "error", text: messageErreur }); toast.error(messageErreur);
    } finally { window.clearTimeout(timeout); setRunning(false); }
  };

  const uploadFiles = async (files) => {
    const selectedFiles = Array.from(files || []).slice(0, 10);
    if (!selectedFiles.length || !selected || uploading) return;
    setUploading(true);
    let uploaded = 0;
    try {
      for (const file of selectedFiles) {
        const data = new FormData(); data.append("file", file);
        try {
          const response = await axios.post(`${API_URL}/api/atelier/agents/${selected.id}/files`, data, {
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
          });
          setPendingFiles((old) => [...old.filter((item) => item.id !== response.data.id), response.data]);
          uploaded += 1;
        } catch (error) { toast.error(`${file.name} : ${error.response?.data?.detail || "envoi impossible"}`); }
      }
      if (uploaded) {
        toast.success(`${uploaded} fichier${uploaded > 1 ? "s" : ""} joint${uploaded > 1 ? "s" : ""} au prochain message`);
        await loadAgents(selected.id);
      }
    } finally { setUploading(false); }
  };

  const uploadFile = async (event) => {
    const files = event.target.files;
    event.target.value = "";
    await uploadFiles(files);
  };

  const dropFiles = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    await uploadFiles(event.dataTransfer.files);
  };

  return (
    <CRMLayout>
      <div className="flex min-h-[calc(100dvh-4rem)] flex-col p-4 lg:h-screen lg:min-h-[680px] lg:p-7">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-indigo-600" /> Agents</h1>
            <p className="text-sm text-slate-500 mt-1">Chaque agent conserve sa mission, ses accès, ses règles et sa mémoire. Chaque nouvelle mission ouvre une session distincte.</p>
          </div>
          <Button onClick={() => setAssistantCreationOuvert(true)} className="w-full sm:w-auto"><Plus className="w-4 h-4 mr-2" /> Créer un agent</Button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center"><Loader2 className="w-7 h-7 text-indigo-600 animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-[250px_220px_minmax(0,1fr)] gap-3 flex-1 min-h-0">
            <section className="bg-white border border-slate-200 rounded-xl p-3 overflow-y-auto">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 px-1 mb-2">Mes agents · {agents.length}</div>
              <div className="space-y-2">
                {agents.map((agent) => (
                  <button key={agent.id} onClick={() => setSelectedId(agent.id)}
                          className={`w-full text-left rounded-xl border p-3 transition ${selectedId === agent.id ? "border-indigo-300 bg-indigo-50 shadow-sm" : "border-slate-200 hover:border-slate-300"}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br ${COULEURS[agent.color] || COULEURS.indigo} text-xl flex items-center justify-center shadow-sm`}>{agent.icon || "🤖"}</div>
                      <div className="min-w-0 flex-1"><div className="font-semibold text-sm text-slate-800 leading-tight">{agent.name}</div><div className="text-[11px] text-slate-500 mt-1 line-clamp-2">{agent.mission}</div><div className="mt-1 text-[9px] uppercase tracking-wider text-indigo-500">{agent.engine || "claude"}{agent.company_project ? ` · ${agent.company_project}` : ""}</div></div>
                    </div>
                  </button>
                ))}
                {agents.length === 0 && <div className="text-sm text-center text-slate-400 py-8">Créez votre premier agent.</div>}
              </div>
            </section>

            <section className="bg-white border border-slate-200 rounded-xl p-3 flex flex-col min-h-0">
              {selected ? <>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Sessions</div>
                  <div className="flex gap-1">
                    <button onClick={() => { setFormAgent(selected); setFormOpen(true); }} title="Configurer" className="p-1.5 rounded-md hover:bg-slate-100"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => deleteAgent(selected)} title="Supprimer" className="p-1.5 rounded-md text-red-500 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <button onClick={newConversation} className="w-full mb-3 inline-flex items-center justify-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-3 py-2 text-sm font-semibold"><Plus className="w-4 h-4" /> Nouvelle session</button>
                <div className="flex-1 overflow-y-auto space-y-1">
                  {conversations.map((conv) => (
                    <button key={conv.session_id} onClick={() => openConversation(conv.session_id)} className={`w-full text-left rounded-lg px-2.5 py-2 border ${sessionId === conv.session_id ? "bg-indigo-50 border-indigo-200" : "border-transparent hover:bg-slate-50"}`}>
                      <div className="text-xs font-medium text-slate-700 line-clamp-2">{conv.title}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">{conv.count} msg · {conv.permanent ? "permanente" : "ponctuelle"} · {conv.updated_at ? new Date(conv.updated_at).toLocaleDateString("fr-FR") : ""}</div>
                    </button>
                  ))}
                  {!conversations.length && <div className="text-xs text-slate-400 text-center py-5">Aucune session.</div>}
                </div>
                <div className="border-t border-slate-100 mt-3 pt-3">
                  <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2"><span>Fichiers · {selected.files?.length || 0}</span><button onClick={() => fileRef.current?.click()} className="text-indigo-600 normal-case tracking-normal inline-flex items-center gap-1"><Upload className="w-3.5 h-3.5" /> Ajouter</button></div>
                  <input ref={fileRef} type="file" multiple className="hidden" onChange={uploadFile} />
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {(selected.files || []).slice().reverse().map((file) => <button key={file.id} type="button" title="Joindre au prochain message" onClick={() => setPendingFiles((old) => old.some((item) => item.id === file.id) ? old : [...old, file])} className="flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left text-[11px] text-slate-600 hover:bg-indigo-50 hover:text-indigo-700"><FileText className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{file.name}</span></button>)}
                    {!selected.files?.length && <div className="text-[11px] text-slate-400">Aucun document confié.</div>}
                  </div>
                </div>
              </> : <div className="m-auto text-sm text-slate-400">Sélectionnez un agent.</div>}
            </section>

            <section className="bg-white border border-slate-200 rounded-xl flex flex-col min-h-[500px] min-w-0">
              {selected ? <>
                <div className="border-b border-slate-200 p-3 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${COULEURS[selected.color] || COULEURS.indigo} text-xl flex items-center justify-center`}>{selected.icon || "🤖"}</div>
                  <div className="min-w-0 flex-1"><h2 className="font-bold text-slate-900 truncate">{selected.name}</h2><p className="text-xs text-slate-500 truncate">{selected.mission}</p></div>
                  <div className="hidden md:flex items-center gap-1 text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-1 rounded-full capitalize">{selected.engine || "claude"}</div><button onClick={() => { setFormAgent(selected); setFormOpen(true); }} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-indigo-300 hover:text-indigo-700"><Settings className="h-4 w-4" /> Paramètres</button>
                  {sessionMissionId && <div className="hidden lg:flex items-center gap-1 text-[11px] text-violet-700 bg-violet-50 border border-violet-200 px-2 py-1 rounded-full">{missions.find((mission) => mission.id === sessionMissionId)?.title || "Mission liée"}</div>}
                  <div className={`hidden md:flex items-center gap-1 text-[11px] border px-2 py-1 rounded-full ${sessionPermanent ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-slate-600 bg-slate-50 border-slate-200"}`}><Brain className="w-3.5 h-3.5" /> {sessionPermanent ? "Session permanente" : "Session ponctuelle"}</div>
                </div>
                <div ref={chatRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                  {!events.length && <div className="max-w-xl mx-auto text-center py-12"><Bot className="w-11 h-11 text-indigo-200 mx-auto mb-3" /><h3 className="font-semibold text-slate-800">{selected.name} est prêt</h3><p className="text-sm text-slate-500 mt-2">{sessionId || sessionMissionId ? (sessionPermanent ? "Le contexte de cette session enrichira sa mémoire permanente." : "Cette session reste isolée et n’enrichira pas la mémoire permanente.") : "Démarrez une session et choisissez si son contexte doit être conservé."}</p><div className="mt-5 grid sm:grid-cols-2 gap-2 text-left"><button onClick={() => send("Fais-moi un point de situation dans le cadre de ta mission.")} className="border border-slate-200 rounded-lg p-3 text-xs hover:border-indigo-300 hover:bg-indigo-50">Fais-moi un point de situation.</button><button onClick={() => send("Quels sont les trois sujets prioritaires à surveiller ?")} className="border border-slate-200 rounded-lg p-3 text-xs hover:border-indigo-300 hover:bg-indigo-50">Quels sont les sujets prioritaires ?</button></div></div>}
                  {events.map((event, index) => event.type === "user" ? (
                    <div key={index} className="flex justify-end"><div className="max-w-[85%] bg-indigo-600 text-white rounded-2xl rounded-br-sm px-4 py-2.5 whitespace-pre-wrap text-sm">{event.text}</div></div>
                  ) : event.type === "assistant" ? (
                    <div key={index} className="max-w-[95%] bg-slate-50 border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 whitespace-pre-wrap text-sm text-slate-800">{event.text}</div>
                  ) : event.type === "tool" ? (
                    <div key={index} className="flex items-center gap-2 text-xs text-slate-400 font-mono"><FileText className="w-3.5 h-3.5" /><span className="truncate">{event.text}</span></div>
                  ) : <div key={index} className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{event.text}</div>)}
                  {running && <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> {selected.name} réfléchit…</div>}
                </div>
                <div onDragEnter={(event) => { event.preventDefault(); setDragActive(true); }} onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = "copy"; setDragActive(true); }} onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setDragActive(false); }} onDrop={dropFiles} className={`relative border-t p-3 transition ${dragActive ? "border-indigo-400 bg-indigo-50 ring-2 ring-inset ring-indigo-300" : "border-slate-200"}`}>
                  {dragActive && <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-indigo-50/95 text-sm font-bold text-indigo-700"><Upload className="mr-2 h-5 w-5" />Déposez les fichiers ici</div>}
                  <div className="flex gap-2">
                    <textarea value={input} onChange={(event) => setInput(event.target.value)} rows={2} disabled={running}
                              onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); send(input); } }}
                              placeholder={`Discuter avec ${selected.name}…`}
                              className="flex-1 resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                    {running ? <BoutonArreter className="self-end" onClick={async () => { arretManuelRef.current = true; try { await fetch(`${API_URL}/api/agent/stop`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ run_id: executionRef.current }) }); } catch { /* flux coupé quand même */ } controleurRef.current?.abort(); }} /> : <Button onClick={() => send(input)} disabled={uploading || (!input.trim() && !pendingFiles.length)} className="self-end">{uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>}
                  </div>
                  {pendingFiles.length > 0 && <div className="mt-2 flex flex-wrap gap-2">{pendingFiles.map((file) => <span key={file.id} className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-[11px] font-medium text-indigo-700"><FileText className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{file.name}</span><button type="button" disabled={running} onClick={() => setPendingFiles((old) => old.filter((item) => item.id !== file.id))} title="Retirer du message" className="rounded-full p-0.5 hover:bg-indigo-100"><X className="h-3 w-3" /></button></span>)}</div>}
                  <div className="mt-1.5 flex items-center justify-between gap-2 text-[10px] text-slate-400"><span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Lecture seule : cet agent observe et conseille, sans modifier le serveur.</span><span className="inline-flex items-center gap-1"><Upload className="h-3 w-3" /> Glissez vos fichiers dans ce champ</span></div>
                </div>
              </> : <div className="m-auto text-center text-slate-400"><Bot className="w-10 h-10 mx-auto mb-2" />Créez ou sélectionnez un agent.</div>}
            </section>
          </div>
        )}
      </div>
      {formOpen && <AgentForm agent={formAgent} onClose={() => { setFormOpen(false); setFormAgent(undefined); }} onSave={saveAgent} />}
      <AgentCreationWizard ouvert={assistantCreationOuvert} fermer={() => setAssistantCreationOuvert(false)} creer={createGuidedAgent} />
      {sessionSetupOpen && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4">
        <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl">
          <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
            <div><h2 className="text-lg font-bold text-slate-900">Démarrer une session</h2><p className="mt-1 text-xs text-slate-500">Choisissez sa mission et ce que l’agent devra retenir.</p></div>
            <button type="button" onClick={() => setSessionSetupOpen(false)} className="rounded-lg p-2 hover:bg-slate-100"><X className="h-5 w-5" /></button>
          </div>
          <div className="space-y-5 p-6">
            <label className="block text-sm font-medium text-slate-700">Mission liée
              <select value={sessionMissionId} onChange={(event) => {
                const missionId = event.target.value;
                const mission = missions.find((item) => item.id === missionId);
                setSessionMissionId(missionId);
                if (mission) setSessionPermanent(Boolean(mission.permanent));
              }} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm">
                <option value="">Session libre, sans mission existante</option>
                {missions.filter((mission) => !mission.agent_id || mission.agent_id === selectedId).map((mission) => <option key={mission.id} value={mission.id}>{mission.title}{mission.project_name ? ` · ${mission.project_name}` : ""}</option>)}
              </select>
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <button type="button" onClick={() => setSessionPermanent(false)} className={`rounded-xl border p-4 text-left ${!sessionPermanent ? "border-indigo-400 bg-indigo-50 ring-1 ring-indigo-200" : "border-slate-200"}`}><div className="font-bold text-slate-800">Session ponctuelle</div><div className="mt-1 text-xs text-slate-500">Le travail reste dans cet échange et ne modifie pas la mémoire.</div></button>
              <button type="button" onClick={() => setSessionPermanent(true)} className={`rounded-xl border p-4 text-left ${sessionPermanent ? "border-emerald-400 bg-emerald-50 ring-1 ring-emerald-200" : "border-slate-200"}`}><div className="flex items-center gap-2 font-bold text-slate-800"><Brain className="h-4 w-4 text-emerald-600" />Session permanente</div><div className="mt-1 text-xs text-slate-500">Les décisions et le contexte utiles enrichissent la mémoire.</div></button>
            </div>
          </div>
          <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4"><Button variant="outline" onClick={() => setSessionSetupOpen(false)}>Annuler</Button><Button onClick={startConversation}>Démarrer avec {selected?.name}</Button></div>
        </div>
      </div>}
    </CRMLayout>
  );
}
